import { Tooltip } from 'antd';
import { Check } from '@phosphor-icons/react';
import type { TierColor } from './api';
import { tokens } from '../../theme';

const sameHex = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

interface TierChipProps {
  name: string;
  colorHex?: string | null;
}

/**
 * Chip tên hạng trên màu hạng, chữ trắng — đúng như khách thấy trên thẻ thành viên. Mọi màu trong
 * bảng đạt AA với chữ trắng (máy chủ khóa bằng test), nên ở đây không tự tính màu chữ.
 */
export function TierChip({ name, colorHex }: TierChipProps) {
  if (!colorHex) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: tokens.radius.pill,
          border: `1px dashed ${tokens.color.line2}`,
          color: tokens.color.ink2,
          fontSize: 12,
          whiteSpace: 'nowrap',
        }}
      >
        Màu mặc định
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: tokens.radius.pill,
        background: colorHex,
        // Chữ trên màu hạng luôn trắng — dùng token nền trắng thay vì viết cứng mã màu.
        color: tokens.color.surface,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {name || 'Tên hạng'}
    </span>
  );
}

interface TierColorPickerProps {
  /** Form.Item bơm vào. Rỗng = chưa chọn, máy chủ gán màu mặc định. */
  value?: string | null;
  onChange?: (hex: string) => void;
  colors: TierColor[];
  /** Màu đã có hạng khác trong CÙNG bản dùng → tên hạng đó. Khóa sẵn để khỏi bấm lưu rồi mới biết. */
  takenBy: Map<string, string>;
  levelName: string;
  disabled?: boolean;
}

/**
 * Chọn màu hạng trong bảng máy chủ trả về. Không có ô nhập hex, không có color picker tự do: màu
 * ngoài bảng có thể không đọc được chữ trắng, hoặc trùng sắc thương hiệu và màu trạng thái.
 *
 * Ô đang chọn có dấu tick chứ không chỉ viền màu — không phân biệt trạng thái chỉ bằng màu.
 */
export function TierColorPicker({
  value,
  onChange,
  colors,
  takenBy,
  levelName,
  disabled,
}: TierColorPickerProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div role="radiogroup" aria-label={`Màu hạng ${levelName}`.trim()} style={{ display: 'flex', gap: 2 }}>
        {colors.map((color) => {
          const selected = sameHex(value, color.hex);
          const owner = takenBy.get(color.hex.toLowerCase());
          const locked = disabled || (!!owner && !selected);

          const swatch = (
            <button
              key={color.hex}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={owner && !selected ? `${color.name}, hạng ${owner} đang dùng` : color.name}
              aria-disabled={locked}
              onClick={() => {
                if (!locked) onChange?.(color.hex);
              }}
              // 44px là vùng bấm, ô màu bên trong 28px — đủ tay trên màn cảm ứng mà vẫn gọn.
              style={{
                width: 44,
                height: 44,
                padding: 0,
                display: 'grid',
                placeItems: 'center',
                border: 'none',
                background: 'transparent',
                cursor: locked ? 'not-allowed' : 'pointer',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: tokens.radius.pill,
                  background: color.hex,
                  opacity: owner && !selected ? 0.3 : 1,
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: selected ? `0 0 0 2px ${tokens.color.surface}, 0 0 0 4px ${color.hex}` : 'none',
                }}
              >
                {selected && <Check size={16} weight="bold" color={tokens.color.surface} />}
              </span>
            </button>
          );

          const tip = owner && !selected ? `Hạng ${owner} đang dùng màu này.` : color.name;
          return (
            <Tooltip key={color.hex} title={tip}>
              {swatch}
            </Tooltip>
          );
        })}
      </div>
      <TierChip name={levelName} colorHex={colors.find((c) => sameHex(c.hex, value))?.hex ?? null} />
    </div>
  );
}
